import WebApp from '@twa-dev/sdk'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Telegram-Init-Data': WebApp.initData,
    ...(options.headers as Record<string, string>),
  }

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`${res.status}: ${text}`)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get:    <T>(path: string)                     => request<T>(path),
  post:   <T>(path: string, body?: unknown)     => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch:  <T>(path: string, body?: unknown)     => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string)                     => request<T>(path, { method: 'DELETE' }),
}

// ── Типы ответов API ──────────────────────────────────────────────────────────

export type MemoryCategory = 'personal' | 'work' | 'business' | 'misc'
export type VoicePreference = 'onyx' | 'nova' | 'none'

export type CallStatus =
  | 'initiated'
  | 'ringing'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'no_answer'
  | 'cancelled'
  | 'answering_machine'

export type CallDirection = 'outgoing' | 'incoming'

export interface CallItem {
  id: number
  direction: CallDirection
  target_phone: string
  goal: string
  status: CallStatus
  result: string | null
  duration_sec: number | null
  recording_url: string | null
  created_at: string
  completed_at: string | null
}

export interface CallDetail extends CallItem {
  transcript: { speaker: string; text: string; ts: string }[]
}

export interface FactItem {
  id: number
  content: string
  categories: MemoryCategory[]
  tags: string[]
  source: string
  created_at: string
}

export type RecurrenceType = 'daily' | 'weekly' | 'monthly' | 'weekdays'

export type ReminderTrigger = 'time' | 'on_wake'

export interface ReminderItem {
  id: number
  text: string
  /** null для напоминаний с trigger_type='on_wake' (без точного времени) */
  remind_at: string | null
  status: string
  recurrence: RecurrenceType | null
  trigger_type: ReminderTrigger
}

export interface MessageItem {
  id: number
  role: 'user' | 'assistant'
  content: string
  content_type: string
  created_at: string
}

export type SubscriptionTier = 'free' | 'plus' | 'pro'

export interface SubscriptionInfo {
  tier: SubscriptionTier
  expires_at: string | null
  source: string | null
  // Активная скидка промокода: процент и tier ('plus'/'pro'/null=любой).
  discount_percent?: number | null
  discount_tier?: string | null
}

export interface CallPackOption {
  size: number
  stars: number
  label: string
}

export interface TaskItem {
  id: number
  title: string
  description: string | null
  status: string
  priority: 'low' | 'normal' | 'high'
  due_at: string | null
  tags: string[]
  created_at: string
}

// ── Календарь и события ───────────────────────────────────────────────────────

export type EventSource = 'delo' | 'google' | 'apple'

export interface EventItem {
  id: number
  title: string
  description: string | null
  location: string | null
  starts_at: string
  ends_at: string | null
  all_day: boolean
  source: EventSource
  /** Куда событие ушло при создании/изменении: 'google' | 'apple' */
  synced: string[]
  /** Подключённые календари, в которые синхронизация не удалась */
  sync_failed: string[]
}

export interface EventPayload {
  title: string
  starts_at: string
  ends_at?: string | null
  description?: string | null
  location?: string | null
  all_day?: boolean
}

export interface CalendarDay {
  reminders: Array<{ id: number; text: string; remind_at: string; recurrence: RecurrenceType | null; trigger_type?: ReminderTrigger }>
  tasks: Array<{ id: number; title: string; due_at: string | null; priority: string; status: string }>
  events: Array<{
    id: number
    title: string
    starts_at: string
    ends_at: string | null
    location: string | null
    source: EventSource
  }>
}

export interface CalendarData {
  days: Record<string, CalendarDay>
  undated_reminders_count: number
  undated_tasks_count: number
}

export interface SyncResult {
  imported: number
  /** Сколько локальных зеркал удалено вслед за внешним календарём. */
  removed: number
  /** Подключённые внешние календари: 'google' | 'apple'. Пустой — синхронизировать нечего. */
  calendars: string[]
}

export const calendarApi = {
  getCalendar: (from: string, to: string) =>
    api.get<CalendarData>(`/api/calendar?from=${from}&to=${to}`),

  /** Тянет события из Google/Apple прямо сейчас, не дожидаясь планировщика. */
  syncEvents: () => api.post<SyncResult>('/api/events/sync', {}),

  createEvent: (data: EventPayload) => api.post<EventItem>('/api/events', data),
  updateEvent: (id: number, data: Partial<EventPayload>) =>
    api.patch<EventItem>(`/api/events/${id}`, data),
  deleteEvent: (id: number) => api.delete<void>(`/api/events/${id}`),

  createReminder: (data: {
    text: string
    remind_at?: string | null
    recurrence?: RecurrenceType | null
    trigger_type?: ReminderTrigger
  }) => api.post<ReminderItem>('/api/reminders', data),
  updateReminder: (
    id: number,
    data: { text?: string; remind_at?: string; recurrence?: RecurrenceType | null },
  ) => api.patch<ReminderItem>(`/api/reminders/${id}`, data),
  deleteReminder: (id: number) => api.delete<void>(`/api/reminders/${id}`),

  createTask: (data: {
    title: string
    description?: string | null
    due_at?: string | null
    priority?: string
    tags?: string[]
  }) => api.post<TaskItem>('/api/tasks', data),
  updateTask: (
    id: number,
    data: {
      title?: string
      due_at?: string | null
      priority?: string
      tags?: string[]
      status?: string
    },
  ) => api.patch<TaskItem>(`/api/tasks/${id}`, data),
  deleteTask: (id: number) => api.delete<void>(`/api/tasks/${id}`),
}

export interface FileItem {
  id: number
  filename: string
  file_type: string | null
  file_size: number | null
  created_at: string
}

export interface IntegrationItem {
  id: number
  type: 'gmail' | 'google_calendar' | 'yandex_mail' | 'apple_calendar'
  email: string | null
  gmail_scope: 'full' | 'readonly' | null
  is_active: boolean
  created_at: string
}

export interface TodayData {
  user: { first_name: string | null; username: string | null }
  today_reminders: Array<{ id: number; text: string; remind_at: string }>
  today_tasks: Array<{ id: number; title: string; priority: string; due_at: string | null }>
  recent_facts: Array<{ id: number; content: string }>
  integrations: Array<{ type: string; email: string | null }>
  subscription: { tier: SubscriptionTier; expires_at: string | null }
  usage_today: { text_messages: number; voice_messages: number }
  limits: { text_messages: number | null; voice_messages: number | null }
  days_since_registration: number
}

export interface FeedbackPayload {
  text?: string
  rating?: number
}

export interface MeData {
  id: number
  telegram_id: number
  username: string | null
  first_name: string | null
  is_admin: boolean
  voice_preference: VoicePreference
  morning_brief_enabled: boolean
  evening_checkin_enabled: boolean
  notif_expiry: boolean
  notif_digest: boolean
  notif_referral: boolean
  notif_limit_warning: boolean
  notif_facts: boolean
  notif_events: boolean
  mail_scan_enabled: boolean
  has_paused_integrations: boolean
  subscription: { tier: SubscriptionTier; expires_at: string | null }
  usage: {
    text_messages_used: number
    text_messages_limit: number | null
    voice_messages_used: number
    voice_messages_limit: number | null
    facts_used: number
    facts_limit: number | null
    reminders_used: number
    reminders_limit: number | null
    tasks_used: number
    tasks_limit: number | null
  }
}

// ── Stats (Фаза 3, Блок D) ───────────────────────────────────────────────────

export interface DayCount {
  date: string   // "YYYY-MM-DD"
  count: number
}

export interface StatsData {
  tasks_completed_total: number
  facts_total: number
  reminders_completed_total: number
  days_with_delo: number
  messages_by_day: DayCount[]
  memory_by_category: {
    personal: number
    work: number
    business: number
    misc: number
  }
}

// ── Admin types ──────────────────────────────────────────────────────────────

export interface AdminUser {
  id: number
  telegram_id: number
  username: string | null
  first_name: string | null
  last_name: string | null
  is_admin: boolean
  is_blocked: boolean
  subscription_tier: SubscriptionTier
  subscription_expires_at: string | null
  last_active_at: string
  created_at: string
}

export interface AdminUserDetail extends AdminUser {
  messages_count: number
  facts_count: number
  reminders_active: number
  tasks_total: number
  tasks_done: number
  cost_30d_usd: number
}

export interface AdminStats {
  total_users: number
  active_today: number
  active_7d: number
  new_7d: number
  new_today: number
  active_pro: number
  paid_users: number
  conversion_rate: number
  messages_24h: number
  facts_total: number
  cost_7d_usd: number
  revenue_7d_stars: number
}

export interface AdminUsageTopUser {
  user_id: number
  telegram_id: number
  username: string | null
  first_name: string | null
  total_cost_usd: number
  total_calls: number
}

export interface AdminUsage {
  period_days: number
  total_cost_usd: number
  total_revenue_stars: number
  by_feature: Record<string, number>
  top_users: AdminUsageTopUser[]
}

export interface AdminActivityUser {
  telegram_id: number
  username: string | null
  first_name: string | null
  last_active_at: string
  subscription_tier: string
}

export interface AdminBackup {
  key: string
  size_bytes: number
  last_modified: string
}

export interface AdminDbStat {
  table_name: string
  row_count: number
  size_bytes: number
}

export interface PaginatedUsers {
  items: AdminUser[]
  total: number
  page: number
  limit: number
}

export interface BroadcastResult {
  sent: number
  failed: number
  total: number
}

export interface PromoSchema {
  id: number
  code: string
  promo_type: string
  tier: string
  days: number
  discount_percent: number | null
  max_uses: number | null
  uses_count: number
  is_active: boolean
  created_at: string
}

export interface RevenueData {
  mrr_rub: number
  current_month_rub: number
  last_month_rub: number
  by_tier: Record<string, number>
  daily: Array<{ date: string; amount_rub: number }>
}

// ── Telegram Business (G1/G2) ────────────────────────────────────────────────

export interface TelegramConnectionInfo {
  business_connection_id: string
  is_enabled: boolean
  connected_at: string
  disconnected_at: string | null
  chats_count: number
}

export interface AutoReplyChat {
  chat_id: number
  title: string | null
  type: 'private' | 'group' | 'supergroup'
  auto_reply: boolean
  last_message_at: string | null
}

export interface AutoReplySettings {
  enabled: boolean
  mode: 'auto' | 'approval'
  style: 'first_person' | 'assistant'
  /** Расшифровка своих голосовых уходит в тот же чат от лица владельца. */
  voice_transcript: boolean
  /** Расшифровка голосовых и кружков собеседника — ответом в том же чате. */
  incoming_voice_transcript: boolean
  /** Удалённые и изменённые сообщения из чатов приходят владельцу в личку. */
  deleted_capture: boolean
  chats: AutoReplyChat[]
}

// ── YooKassa / card payments ──────────────────────────────────────────────────

export interface PaymentAvailable {
  available: boolean
}

export interface CreatePaymentResult {
  payment_id: number
  confirmation_url: string
  amount_rub: number
}

export type CardPaymentStatus = 'pending' | 'succeeded' | 'canceled'

/** Годовые тарифы для карточной оплаты */
export type YearlyTier = 'plus_yearly' | 'pro_yearly'
/** Всё, что можно купить картой */
export type PayableTier = SubscriptionTier | YearlyTier

export interface PaymentStatus {
  payment_id: number
  yookassa_id: string
  tier: string
  amount_rub: number
  status: CardPaymentStatus
}

// ── Referral ─────────────────────────────────────────────────────────────────

export interface ReferralEarning {
  commission_rub: number
  commission_percent: number
  payment_month: number
  source: string
  created_at: string
}

export interface ReferralData {
  code: string
  link: string
  referral_count: number
  balance_rub: number
  total_earned_rub: number
  earnings_history: ReferralEarning[]
}

// ── Тарифы ───────────────────────────────────────────────────────────────────

/** Ключ покупаемого плана: тариф + период. Совпадает с backend/app/tariffs.py. */
export type PlanKey = 'plus' | 'pro' | 'plus_yearly' | 'pro_yearly'

export interface PricingPlan {
  key: PlanKey
  tier: 'plus' | 'pro'
  period: 'month' | 'year'
  days: number
  stars: number
  rub: number
  title: string
}

export interface Pricing {
  plans: PricingPlan[]
  yearly_discount_percent: number
}

export const pricingApi = {
  /** Актуальные цены. Единственный источник — backend/app/tariffs.py. */
  get: () => api.get<Pricing>('/api/subscription/pricing'),
}

export const paymentApi = {
  available: () => api.get<PaymentAvailable>('/api/payment/available'),
  /** Создать платёж для тарифа подписки */
  create: (tier: PayableTier) =>
    api.post<CreatePaymentResult>('/api/payment/create', { tier }),
  status: (paymentId: number) =>
    api.get<PaymentStatus>(`/api/payment/status/${paymentId}`),
}
